import { useEffect, useRef, useState } from 'react'
import { useSelector } from "react-redux"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { history } from 'services/history.js'
import { formatMoney, showLoading } from 'services/util'
import store from 'redux/store'
import { setLiveAuctionCode } from '../redux/actions'

import { getAuctionByCode, updateUserRegistration, registerUserForAuction, getAuctionUserApi } from '../api'
import { urlQueryParser } from 'services/util'
import { isAttending } from '../util/userUtils'
import { updateLiveBid, purchaseFixedItem, makeDonation } from '../auctionUtils'

import themeCss from '../components/sass/theme.module.scss'
import css from './sass/auctions.module.scss'
import auctionCardCss from './sass/auctionscards.module.scss'

import AuctionCountdown from './AuctionCountdown'
import ItemCard from '../items/ItemCard'
import ConfirmBidModal from '../components/ConfirmBidModal'
import CustomMarkdown from 'sites/auctions/components/CustomMarkdown'

import TextMessageOff from './TextMessageOff'
import TextMessageOn from './TextMessageOn'
import AuctionQrCard from './AuctionQrCard'
import CategoryFilter from './CategoryFilter'
import AuctionDisclosureFooter from './AuctionDisclosureFooter'

import { getAuctionUser, getBidSubscriptionResponse, getFixedItemSubscriptionResponse, getDonationSubscriptionResponse } from '../redux/selectors'
import BidSuccessNotification from '../components/BidSuccessNotification'
import AuctionStatus from '../components/AuctionStatus'
import AuctionEndedCard from './AuctionEndedCard'
import AuctionStatusClosed from './AuctionStatusClosed'
import UnregisterButton from './UnregisterButton'
import ConfirmDonationModal from '../components/ConfirmDonationModal'
import ConfirmFixedItemModal from '../components/ConfirmFixedItemModal'
import FixedItemCard from '../items/FixedItemCard'
import DonationCard from './DonationCard'
import { calculateUserBalance, userItemPaidStatus } from '../util/endedAuctionUtils'
import AuctionEndedCardPaid from './AuctionEndedCardPaid'
import ItemFilter from './ItemFilter'
import DonationButton from './DonationButton'
import { clearAuctionScrollRestore, getAuctionScrollRestore } from '../util/auctionScrollRestore'
import { getFilteredAuctionItems } from './categoryFilterOptions'
import { getItemsInDisplayOrder } from '../components/utils'

export default function Auction(props) {
  //destructuring auction code from url
  //called id in url query for that convention, but using auction code in aws to declutter url from being massive from aws UUID for auction ID to link tables
  const { auctionId: auctionCode, donation: donationQueryParam } = urlQueryParser(props.location.search)

  const [date, setDate] = useState(new Date())
  const [auctionState, setAuctionState] = useState()
  const [textAlertNumber, setTextAlertNumber] = useState()
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [userAttending, setUserAttending] = useState()
  const [displayItems, setDisplayItems] = useState([])

  //bid and notifications
  const [showBidModal, setShowBidModal] = useState()
  const [bidItem, setBidItem] = useState()
  const [bidAmount, setBidAmount] = useState()
  const [bidResponse, setBidResponse] = useState()

  //fixed item
  const [showFixedItemModal, setShowFixedItemModal] = useState(false)
  const [purchaseItem, setPurchaseItem] = useState()
  const [purchaseQuantity, setPurchaseQuantity] = useState()
  const [purchaseResponse, setPurchaseResponse] = useState()

  const [showDonationModal, setShowDonationModal] = useState()
  const [donationAmount, setDonationAmount] = useState()
  const [donationResponse, setDonationResponse]= useState()
  const [hasProcessedDonationQuery, setHasProcessedDonationQuery] = useState(false)
  
  const [outstandingBalance, setOutstandingBalance] = useState()

  const [successItem, setSuccessItem] = useState()
  const successBannerTime = 7

  // for test / demo purposes
  const [forced, setForced] = useState('')
  const [iframeAction, setIframeAction]= useState('')

  const auctionUser = useSelector(getAuctionUser);
  const currentUserId = auctionUser?.UserId
  const [categoryFilter, setCategoryFilter] = useState('')

  const bidSubscriptionResponse = useSelector(getBidSubscriptionResponse);
  const fixedItemSubscriptionResponse = useSelector(getFixedItemSubscriptionResponse);
  const donationSubscriptionResponse = useSelector(getDonationSubscriptionResponse);

  //search / filter for items display
  const [filterInput, setFilterInput] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const handledScrollLocationKey = useRef()

  //to refresh query on changes
  const queryClient = useQueryClient()

  //for simulation buttons
  // const isAuctionAdmin = localStorage.getItem('isAuctionAdmin')
  // load auction data by code, so it will work for deep-linking
  const { data: auctionDetail, error, isLoading } = useQuery({
    queryKey: ["auctionDetail", auctionCode],
    queryFn: async () => {
      const auction = await getAuctionByCode(auctionCode)
      return auction
    },
    staleTime: 0
  })

  useEffect(() => {
    const restoreState = getAuctionScrollRestore(props.location)
    const locationKey = `${props.location.pathname}:${props.location.search}`

    if (!auctionDetail || handledScrollLocationKey.current === locationKey) return

    handledScrollLocationKey.current = locationKey

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (restoreState?.auctionCode === auctionCode) {
          window.scrollTo(0, restoreState.scrollY || 0)
        }
        else {
          window.scrollTo(0, 0)
        }

        clearAuctionScrollRestore(props.location)
      })
    })
  }, [auctionDetail, auctionCode, props.location])

  // poll for auction user every 15 seconds if user is not checked in and Auction is upcoming within a day, or is running
  useEffect(() => {
    let timer

    function clearTimer() {
      if (timer) {
        console.log('clearing timer')
        clearInterval(timer)
      }
    }

    async function refreshCheckinStatus() {
      if (auctionDetail
          && !isCheckedIn
          && (auctionState === 'started'
              || (auctionState === 'before'
                  && (new Date(auctionDetail?.StartTime).getTime() - new Date().getTime()) > 1000*60*60*24))
        ) {

        console.log('refreshing checkin')

        timer = setInterval(async () => {
          console.log('checking auction user')
          await getAuctionUserApi()
        }, 1000*15)
      }
      else {
        console.log('not refreshing checkin')
        clearTimer()
      }
      return
    }

    refreshCheckinStatus()

    return clearTimer

  }, [auctionDetail, isCheckedIn, auctionState])

  useEffect(() => {
    if (props.location.outbidNotification) {
      setShowBidModal(props.location.outbidNotification.showBidModal)
      setBidItem(props.location.outbidNotification.outbidItem)
      setBidAmount(props.location.outbidNotification.bidAmount)
    }

  }, [props.location.outbidNotification])

  // redux liveAuctionCode is consumed by <OutBidNotification> set in the theme header (and could be by other global widgets)
  // I think it's best to only set auctionCode and force the widget to get a fresh version of the auction (since some things can change)
  // system assumes only one live auction at a time, which I think is a fair assumption to not add tons of code to handle multiple live auctions
  useEffect(() => {
    if (auctionState === 'started') {
      store.dispatch(setLiveAuctionCode(auctionCode))
    }
    else {
      store.dispatch(setLiveAuctionCode())
    }
   }, [auctionState, auctionCode])

  //use effect for query results
  useEffect(() => {
    showLoading(isLoading)

    if (error || (auctionDetail && auctionDetail.error)) {
      console.log('Error!', error || auctionDetail.error)
    }

  }, [auctionDetail, error, isLoading])

  //calculate the user's balance
  useEffect(() => {
    setOutstandingBalance(calculateUserBalance(auctionUser, auctionDetail))
  }, [auctionDetail, auctionUser])
  
  //is the user attending
  useEffect(() => {
    const isUserAttending = isAttending(auctionDetail?.AuctionId, auctionUser)
    setUserAttending(isUserAttending)
  }, [auctionDetail, auctionUser])

  // TODO - I'm not sure this is the best way to determine stuff like isCheckedIn, since the flow could always change
  //event listener for iframe to hard code values
  useEffect(() => {
    if (iframeAction) {
      if (iframeAction === 'notAttending') setUserAttending(false)
      if (iframeAction === 'attending') setUserAttending(true)
      if (iframeAction === 'checkIn') setIsCheckedIn(true)
      if (iframeAction === 'checkOut') setIsCheckedIn(false)
      if (iframeAction === 'auctionLive') setForced('live')
      if (iframeAction === 'auctionEnd') setForced('end')
    }
  }, [iframeAction])

  //checking registration for checked in status and text alert status
  useEffect(() => {
    if (!auctionDetail || !auctionUser) return

    const myRegistration = auctionUser.MyAuctions.filter(registration => {
      return registration.Auction.AuctionId === auctionDetail.AuctionId
    })[0]


    if (myRegistration) {
      setTextAlertNumber(myRegistration.TextAlertNumber)
      setIsCheckedIn(myRegistration.IsCheckedIn)
    }
    
  }, [auctionDetail, auctionUser])

  useEffect(() => {
    const shouldAutoOpenDonationModal = donationQueryParam?.toLowerCase() === 'true'

    if (!shouldAutoOpenDonationModal || hasProcessedDonationQuery) return
    if (!auctionDetail || !auctionUser || !auctionState) return

    const canAutoOpenDonationModal = auctionDetail.HasDonation === true
      && userAttending
      && auctionState === 'started'

    if (canAutoOpenDonationModal) {
      setShowDonationModal(true)
    }

    setHasProcessedDonationQuery(true)
  }, [auctionDetail, auctionState, auctionUser, donationQueryParam, hasProcessedDonationQuery, userAttending])

  const updateStatus = (status) => {
    setAuctionState(status)
  }

  const updateBidModalAmount = (itemBidAmount) => {
    setBidAmount(itemBidAmount)
  }

  useEffect(() => {
    const performUserFilter = (items) => {
      if (filterInput.length === 0) return items
      return items.filter(item => item.Title.toLowerCase().includes(filterInput.toLowerCase()))
    }

    if (!auctionDetail) return

    const filteredItems = getFilteredAuctionItems({
      bidItems: auctionDetail.Items,
      fixedItems: auctionDetail.FixedItems,
      categoryFilter,
    })

    setDisplayItems(getItemsInDisplayOrder({
      auction: auctionDetail,
      items: performUserFilter(filteredItems.bidItems),
      fixedItems: performUserFilter(filteredItems.fixedItems),
    }))

  }, [auctionDetail, categoryFilter, filterInput])

  const submitBid = async (item, bidAmount) => {
    showLoading(true)
  
    let updatedItem = {}
  
    const bidFields = {
      itemId: item.ItemId,
      bidAmount,
    }
    try {
      const response = await updateLiveBid(bidFields)
      setBidResponse(response)

      if (response.error) {
        console.log('errored', response)
      } else {
        console.log('passed', response)
        updatedItem = {...item, ...response, timeOut: new Date(date.getTime() + (successBannerTime * 1000))}
        setSuccessItem(updatedItem)
        queryClient.invalidateQueries(["auctionDetail"])
      }
    }
    catch(e) {
      setBidResponse(e)
    }
    //reset bid response for downstream checks on bid response
    showLoading(false)
  }

  const submitDonation = async (donationAmount) => {
    showLoading(true)

    try {
      console.log({donationAmount, auctionDetail: auctionDetail.AuctionId})
      const response = await makeDonation(donationAmount, auctionDetail.AuctionId)
      console.log({response})
      setDonationResponse(response)

      if (response.error) {
        console.log('errored', response)
      } else {
        console.log('passed', response)
        queryClient.invalidateQueries(["auctionDetail"])
      }
    }
    catch(e) {
       setDonationResponse(e)
    }
    //reset donation response for downstream checks on donation response
    showLoading(false)
  }

  const submitPurchase = async (item, purchaseQuantity) => {

    showLoading(true)
  
    let updatedItem = {}
    
    const purchaseFields = {
      quantity: purchaseQuantity,
      fixedItemId: item.FixedItemId,
    }
    try {
      const response = await purchaseFixedItem(purchaseFields)
      setPurchaseResponse(response)

      console.log('purchase reponse', response)

      if (response.error) {
        console.log('errored', response)
      } else {
        console.log('passed', response)
        updatedItem = {...item, ...response, timeOut: new Date(date.getTime() + (successBannerTime * 1000))}
        
        // TO DO - update for successful purchase banner
        // setSuccessItem(updatedItem)
        queryClient.invalidateQueries(["auctionDetail"])
      }
    }
    catch(e) {
      setPurchaseResponse(e)
    }
    //reset bid response for downstream checks on bid response
    showLoading(false)
  }

  //use effect if bidSubscriptionResponse changes (item is bid on by anyone)
  useEffect(() => {

    //if item bid on is in auction, update the auction details
    if (auctionDetail && bidSubscriptionResponse?.ItemId) {

      const isInAuction = (item) => item.ItemId === bidSubscriptionResponse.ItemId
      //if bid response item id is in auction items array
      if (auctionDetail.Items.findIndex(isInAuction) > -1) {
        //invalid query to repull
        queryClient.invalidateQueries(["auctionDetail"])
      }
    }

  }, [bidSubscriptionResponse, auctionDetail, queryClient])

  //use effect if fixedItemSubscriptionResponse changes (fixed item is purchased by anyone)
  useEffect(() => {

    //if item bid on is in auction, update the auction details
    if (auctionDetail && fixedItemSubscriptionResponse?.FixedItemId) {

      const isInAuction = (item) => item.FixedItemId === fixedItemSubscriptionResponse.FixedItemId
      //if bid response item id is in auction items array
      if (auctionDetail.FixedItems.findIndex(isInAuction) > -1) {
        //invalid query to repull
        queryClient.invalidateQueries(["auctionDetail"])
      }
    }

  }, [fixedItemSubscriptionResponse, auctionDetail, queryClient])

  //use effect if donationSubscriptionResponse changes (donation has been made by anyone)
  useEffect(() => {
    //if item bid on is in auction, update the auction details
    if (auctionDetail && donationSubscriptionResponse?.AuctionId) {

      // I think this is filtered if there are two auctions going on, but I'm not 100% sure
      const isInAuction = auctionDetail.AuctionId === donationSubscriptionResponse.AuctionId
      if (isInAuction) queryClient.invalidateQueries(["auctionDetail"])
    }

  }, [donationSubscriptionResponse, auctionDetail, queryClient])

  const registerForAuction = async (auction) => {
    showLoading(true)
    const signUpResponse = await registerUserForAuction(auction.AuctionCode)
    showLoading(false)
  }

  // maybe provide a link for if no auction is loaded to reroute to load auctions - 404 something
  if (!auctionDetail) return (<></>)

  //for iframe to update the state variable, to push a refresh of status
  window.addEventListener('message', (event) => {
    // if (event.origin === 'https://your-app-domain.com') { // Check the origin
      const { action } = event.data;
      setIframeAction(action)
    // }
  })

  // send user to home page if auction is still in preview, unless admin user is using Preview Test Site
  if (auctionDetail.Status === 'PREVIEW' && (!localStorage.isAuctionAnyAdmin || !sessionStorage.previewAuctionCode))
    history.push({ pathname: '/' })

  if (auctionDetail.Status === 'CLOSED')
    return <AuctionStatusClosed auctionDetail={auctionDetail} auctionUser={auctionUser} userAttending={userAttending} />

  return (
    <div className={css['auction-container']}>
      <ItemFilter filterInput={filterInput} setFilterInput={setFilterInput} isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />

      <AuctionStatus auction={auctionDetail} updateStatus={updateStatus} forced={forced} />

      {showBidModal && <ConfirmBidModal submitFn={submitBid} item={bidItem} bidAmount={bidAmount} auctionDetail={auctionDetail}
        showBidModal={showBidModal} setShowBidModal={setShowBidModal} bidResponse={bidResponse} setBidResponse={setBidResponse} />}

      {showDonationModal && <ConfirmDonationModal submitFn={submitDonation} auctionDetail={auctionDetail} auctionUser={auctionUser}
        showDonationModal={showDonationModal} setShowDonationModal={setShowDonationModal} 
        donationResponse={donationResponse} setDonationResponse={setDonationResponse} />}
      
      {showFixedItemModal && <ConfirmFixedItemModal submitFn={submitPurchase} item={purchaseItem} 
        purchaseQuantity={purchaseQuantity} auctionDetail={auctionDetail}
        showFixedItemModal={showFixedItemModal} setShowFixedItemModal={setShowFixedItemModal} 
        purchaseResponse={purchaseResponse} setPurchaseResponse={setPurchaseResponse} />}

        <BidSuccessNotification successItem={successItem} auctionDetail={auctionDetail}/>

      <h2 id="landingPageTitle">
        {auctionDetail.Title}
      </h2>
      
      <div className={themeCss['blue-background']}>
        
        {userAttending && <div>
          <AuctionCountdown auctionDetail={auctionDetail} auctionState={auctionState} isCheckedIn={isCheckedIn} />
        </div>}

        {!userAttending &&
          <>
            <CustomMarkdown text={auctionDetail.Description} cssClass={css['auction-description-preview']} />

            <button className={`${themeCss['FilledYellow']} ${themeCss['button']}`}
                onClick={() => registerForAuction(auctionDetail)}
                disabled={auctionState === 'ended'}
              >Sign Up For Auction</button>
        </>}

        {userAttending && auctionDetail.NeedsCheckIn && <AuctionQrCard auction={auctionDetail} isCheckedIn={isCheckedIn} />}

        {!textAlertNumber && userAttending && <TextMessageOff textAlertNumber={textAlertNumber} auctionUser={auctionUser} auction={auctionDetail} />}

        {(auctionState === 'ended' && outstandingBalance?.total > 0) && 
          <AuctionEndedCard auction={auctionDetail} auctionUser={auctionUser} route={'auction'} outstandingBalance={outstandingBalance}/>
        }

        {(auctionState === 'ended' && outstandingBalance?.total === 0) && <AuctionEndedCardPaid auction={auctionDetail} auctionUser={auctionUser} />}

        <DonationButton auction={auctionDetail} userAttending={userAttending} auctionState={auctionState} 
          auctionUser={auctionUser} setShowDonationModal={setShowDonationModal} />

        {(auctionState !== 'ended' && userAttending) && 
          <>
            <CategoryFilter categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} auction={auctionDetail} />
            <p className={css['category-filter-showtext']}> Showing <b>{categoryFilter}</b> ({displayItems.length} items) </p>
          </>}        

        <div className={css['auctionitems-container']}>
          {userAttending && displayItems.map((item) => {
            if (item.ItemId) {
              return <ItemCard key={item.ItemId} item={item} auction={auctionDetail}
                // if user is not attending, force auction state to appear auction has not started
                auctionState={userAttending ? auctionState : 'before'} 
                currentUserId={currentUserId} isCheckedIn={isCheckedIn} userAttending={userAttending}
                setBidItem={setBidItem} setShowBidModal={setShowBidModal} updateBidModalAmount={updateBidModalAmount} 
                />
            }
            if (item.FixedItemId) {
              return <FixedItemCard key={item.FixedItemId} item={item} auction={auctionDetail}
                  // if user is not attending, force auction state to appear auction has not started
                  auctionState={userAttending ? auctionState : 'before'} 
                  currentUserId={currentUserId} isCheckedIn={isCheckedIn}
                  setShowFixedItemModal={setShowFixedItemModal}
                  purchaseResponse={purchaseResponse}
                  setPurchaseItem={setPurchaseItem} setPurchaseQuantity={setPurchaseQuantity}
                />
            }
            return null
          })}

        </div>

        {/* <DonationCard auction={auctionDetail} userAttending={userAttending} auctionState={auctionState} 
          auctionUser={auctionUser} setShowDonationModal={setShowDonationModal} /> */}

        {(auctionState === 'ended' && outstandingBalance?.total > 0) && 
          <div className={themeCss['box-top-yellow-accent-wide']} >
            <p className={auctionCardCss['auction-box-info-heading']}>Thank you for participating!</p>
            <button
                  onClick ={(e) => {history.push({
                    pathname: '/checkout',
                    search: `?auctionId=${auctionDetail.AuctionCode}`,
                    auctionDetail
                  })}}
                  className={`${themeCss['FilledYellow']} ${themeCss['button']} ${css['button-to-checkout']}`}
                >Proceed to Checkout</button>
          </div>
        }

        {textAlertNumber === 'declined' && userAttending && <TextMessageOff textAlertNumber={textAlertNumber} auctionUser={auctionUser} auction={auctionDetail} />}
        {textAlertNumber && userAttending && textAlertNumber !== 'declined' && <TextMessageOn auctionUser={auctionUser} auction={auctionDetail} />}

        {userAttending &&
          <p className={css['auction-description']}>
            <CustomMarkdown text={auctionDetail.Description} cssClass={css['auction-description-preview']} />
          </p>}

        <AuctionDisclosureFooter auctionDetail={auctionDetail}/>

        {/* {userAttending && <UnregisterButton auctionDetail={auctionDetail} auctionUser={auctionUser} />} */}
      </div>
    </div>
  )
}
